import type { DrawOperation, NormPoint, RegionId } from '../core/types';
import type { IArtworkRenderer, LiveStroke } from './IArtworkRenderer';
import { TURTLE_OUTLINE, TURTLE_PAPER_POLYGONS, TURTLE_REGIONS } from '../core/geometry/turtle';
import { buildExportPlan, type ExportBackground } from '../core/export';

/**
 * Canvas 2D implementation of IArtworkRenderer.
 *
 * Layer model (bottom to top):
 *   1. paper   — opaque white fill of the union of all regions (the uncolored turtle)
 *   2. paint   — committed paint/erase ops, composited on their own layer
 *   3. live    — the in-progress stroke (never part of the source of truth)
 *   4. outline — thick dark strokes, always on top; paint/erase can never touch it
 *
 * Erase uses `destination-out` on the PAINT layer only, so it removes color but
 * never the paper or outline. The paint layer is baked once per committed op;
 * per-frame work during a stroke is just two drawImage calls + one live stroke.
 */
export interface RendererOptions {
  paperColor?: string;
  outlineColor?: string;
  /** Outline width as a fraction of artwork width. */
  outlineWidth?: number;
}

export class CanvasArtworkRenderer implements IArtworkRenderer {
  private readonly display: HTMLCanvasElement;
  private readonly displayCtx: CanvasRenderingContext2D;
  private readonly paint: HTMLCanvasElement; // committed paint/erase only
  private readonly paintCtx: CanvasRenderingContext2D;
  private readonly scratch: HTMLCanvasElement; // committed + live preview
  private readonly scratchCtx: CanvasRenderingContext2D;

  private readonly w: number;
  private readonly h: number;
  private readonly paperColor: string;
  private readonly outlineColor: string;
  private readonly outlineWidth: number;

  private readonly regionPaths = new Map<RegionId, Path2D>();
  private live: LiveStroke | null = null;

  private afterRender: (() => void) | null = null;

  constructor(display: HTMLCanvasElement, options: RendererOptions = {}) {
    this.display = display;
    this.w = display.width;
    this.h = display.height;
    this.paperColor = options.paperColor ?? '#ffffff';
    this.outlineColor = options.outlineColor ?? '#1a1a24';
    this.outlineWidth = options.outlineWidth ?? 0.012;

    this.displayCtx = must2d(display);
    this.paint = makeCanvas(this.w, this.h);
    this.paintCtx = must2d(this.paint);
    this.scratch = makeCanvas(this.w, this.h);
    this.scratchCtx = must2d(this.scratch);

    // Pre-build pixel-space Path2D for each region (used for clipping).
    for (const region of TURTLE_REGIONS) {
      this.regionPaths.set(region.id, this.polygonsToPath(region.polygons));
    }

    this.render();
  }

  /** Register a callback fired after every recomposition (e.g. to refresh a GPU texture). */
  setAfterRender(fn: () => void): void {
    this.afterRender = fn;
  }

  // --- IArtworkRenderer ------------------------------------------------------

  reset(): void {
    this.paintCtx.clearRect(0, 0, this.w, this.h);
    this.render();
  }

  apply(op: DrawOperation): void {
    this.strokeInto(this.paintCtx, op.type, op.regionId, op.color, op.size, op.points);
    this.render();
  }

  setLive(op: LiveStroke | null): void {
    this.live = op;
    this.render();
  }

  // --- rendering -------------------------------------------------------------

  private render(): void {
    const ctx = this.displayCtx;
    ctx.clearRect(0, 0, this.w, this.h);

    // 1. paper (white uncolored turtle)
    this.fillPaper(ctx);

    // 2/3. paint layer (committed + optional live preview)
    let paintSource: HTMLCanvasElement = this.paint;
    if (this.live && this.live.points.length > 0) {
      const s = this.scratchCtx;
      s.clearRect(0, 0, this.w, this.h);
      s.drawImage(this.paint, 0, 0);
      this.strokeInto(
        s,
        this.live.type,
        this.live.regionId,
        this.live.color,
        this.live.size,
        this.live.points,
      );
      paintSource = this.scratch;
    }
    ctx.drawImage(paintSource, 0, 0);

    // 4. outline on top — unaffected by paint/erase
    this.strokeOutline(ctx);

    this.afterRender?.();
  }

  private fillPaper(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.fillStyle = this.paperColor;
    for (const poly of TURTLE_PAPER_POLYGONS) {
      ctx.fill(this.polygonToPath(poly));
    }
    ctx.restore();
  }

  private strokeOutline(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.strokeStyle = this.outlineColor;
    ctx.lineWidth = this.outlineWidth * this.w;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (const poly of TURTLE_OUTLINE) {
      ctx.stroke(this.polygonToPath(poly, true));
    }
    ctx.restore();
  }

  /** Draw one paint/erase stroke into a target context, clipped to its region. */
  private strokeInto(
    ctx: CanvasRenderingContext2D,
    type: 'paint' | 'erase',
    regionId: RegionId,
    color: string,
    size: number,
    points: readonly NormPoint[],
  ): void {
    if (points.length === 0) return;
    const clip = this.regionPaths.get(regionId);
    ctx.save();
    if (clip) ctx.clip(clip);
    ctx.globalCompositeOperation = type === 'erase' ? 'destination-out' : 'source-over';
    ctx.strokeStyle = type === 'erase' ? 'rgba(0,0,0,1)' : color;
    ctx.fillStyle = ctx.strokeStyle;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const lineWidth = Math.max(1, size * this.w * 2); // size is a radius fraction
    ctx.lineWidth = lineWidth;

    if (points.length === 1) {
      // A single tap paints a dot.
      const p = points[0];
      ctx.beginPath();
      ctx.arc(p.x * this.w, p.y * this.h, lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.moveTo(points[0].x * this.w, points[0].y * this.h);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x * this.w, points[i].y * this.h);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  private polygonToPath(poly: readonly NormPoint[], closed = true): Path2D {
    const path = new Path2D();
    if (poly.length === 0) return path;
    path.moveTo(poly[0].x * this.w, poly[0].y * this.h);
    for (let i = 1; i < poly.length; i++) {
      path.lineTo(poly[i].x * this.w, poly[i].y * this.h);
    }
    if (closed) path.closePath();
    return path;
  }

  private polygonsToPath(polys: readonly NormPoint[][]): Path2D {
    const path = new Path2D();
    for (const poly of polys) {
      if (poly.length === 0) continue;
      path.moveTo(poly[0].x * this.w, poly[0].y * this.h);
      for (let i = 1; i < poly.length; i++) {
        path.lineTo(poly[i].x * this.w, poly[i].y * this.h);
      }
      path.closePath();
    }
    return path;
  }

  // --- export ----------------------------------------------------------------

  /**
   * Compose a standalone export canvas following the export plan
   * (background -> paper -> paint -> outline). No UI, no debug overlay.
   */
  composeExport(background: ExportBackground = 'white'): HTMLCanvasElement {
    const plan = buildExportPlan(background);
    const out = makeCanvas(this.w, this.h);
    const ctx = must2d(out);
    for (const layer of plan.layers) {
      switch (layer) {
        case 'background':
          ctx.save();
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, this.w, this.h);
          ctx.restore();
          break;
        case 'paper':
          this.fillPaper(ctx);
          break;
        case 'paint':
          ctx.drawImage(this.paint, 0, 0);
          break;
        case 'outline':
          this.strokeOutline(ctx);
          break;
      }
    }
    return out;
  }

  get width(): number {
    return this.w;
  }
  get height(): number {
    return this.h;
  }
  get displayCanvas(): HTMLCanvasElement {
    return this.display;
  }
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function must2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable.');
  return ctx;
}
